import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";

// ✅ message_key only
const translateBackend = (t, data, fallbackKey = "msg_server_or_network") => {
  if (!data) return t(fallbackKey);
  if (data.message_key) return t(data.message_key, data.message_params || {});
  return t("msg_unknown_error");
};

const ForgetPassword = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const API = process.env.REACT_APP_API_URL;
  const authMotionClass =
    location.state?.authDirection === "toForgot" ? "forgot-slide-from-login" : "forgot-slide-soft";

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info"); // "info" | "error"
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Cooldown timer (anti-spam)
  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setMessageType("info");
    setFieldErrors({});

    const vEmail = (email || "").trim();

    // ---------- client validations ----------
    if (!vEmail) {
      setFieldErrors({ email: t("forgot_email_required") });
      setMessage(t("forgot_email_required"));
      setMessageType("error");
      return;
    }

    if (!emailRegex.test(vEmail)) {
      setFieldErrors({ email: t("msg_invalid_email") });
      setMessage(t("msg_invalid_email"));
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/password/forgot`,
        { email: vEmail },
        {
          headers: {
            // ✅ sends language to backend so email can be translated
            "Accept-Language": i18n.language, // fr|en|ar|fa (or fr-FR, etc.)
          },
        }
      );

      // backend returns {message_key: "forgot_email_sent"} (neutral)
      setMessage(translateBackend(t, res?.data, "forgot_email_sent"));
      setMessageType("info");
      setCooldown(30);
    } catch (err) {
      const data = err?.response?.data;

      // keep neutral message for security (anti-enum)
      const txt =
        (data?.message_key && translateBackend(t, data, "forgot_email_sent")) ||
        t("forgot_email_sent");

      setMessage(txt);
      setMessageType("info");
      setCooldown(30);
    } finally {
      setLoading(false);
    }
  };

  const msgClass =
    messageType === "error"
      ? "message message--error"
      : "message message--info";

  return (
    <div className={`forgot-shell ${authMotionClass}`}>
      <section className="forgot-visual-panel">
        <div className="forgot-portal" aria-hidden="true">
          <span className="forgot-ring forgot-ring-one"></span>
          <span className="forgot-ring forgot-ring-two"></span>
          <span className="forgot-scan-line"></span>

          <div className="forgot-mail-card">
            <span>{t("assist_by_scan", "ASSIST BY SCAN")}</span>
            <strong>{t("forgot_send_link", "Envoyer le lien de réinitialisation")}</strong>
          </div>

          <div className="forgot-chip forgot-chip-left">
            {t("forgot_secure_link", "Lien sécurisé")}
          </div>
          <div className="forgot-chip forgot-chip-right">
            {t("forgot_resend_window", "Fenêtre anti-spam")}
          </div>
        </div>

        <div className="auth-visual-copy">
          <p className="auth-eyebrow">{t("assist_by_scan", "ASSIST BY SCAN")}</p>
          <h1>
            {t(
              "forgot_story_title",
              "Récupérez votre accès sans interrompre vos interventions."
            )}
          </h1>
        </div>
      </section>

      <section className="forgot-panel forgot-form-panel">
        <div className="forgot-kicker">
          <span>{t("assist_by_scan", "ASSIST BY SCAN")}</span>
          <strong>{t("forgot_title", "Mot de passe oublié")}</strong>
        </div>

        <div className="forgot-heading">
          <div className="forgot-heading-icon">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <h2 className="forgot-title">{t("forgot_title", "Mot de passe oublié")}</h2>
            <p>
              {t(
                "forgot_intro",
                "Entrez votre email et recevez un lien sécurisé pour créer un nouveau mot de passe."
              )}
            </p>
          </div>
        </div>

        {message && <p className={msgClass}>{message}</p>}

          <form onSubmit={handleSubmit} className="form" noValidate>
            <input
              id="forgot-email"
              type="email"
              placeholder={t("email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input forgot-input"
              aria-invalid={!!fieldErrors.email}
              required
            />

            {fieldErrors.email && (
              <p className="error-text">{fieldErrors.email}</p>
            )}

            <button
              type="submit"
              disabled={loading || cooldown > 0}
              className={`btn btn-lg forgot-submit ${loading || cooldown > 0 ? "btn--muted" : ""}`}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {!loading && <Mail className="h-4 w-4" />}
              {loading
                ? t("forgot_sending")
                : cooldown > 0
                ? t("forgot_resend_in", { seconds: cooldown })
                : t("forgot_send_link")}
            </button>
          </form>

          <div className="forgot-bottom-link">
            <p>
              {t("forgot_remember", "Vous vous en souvenez ?")}{" "}
              <Link to="/login" state={{ authDirection: "toLogin" }}>
                {t("nav_login", "Connexion")}
              </Link>
            </p>
          </div>
      </section>
    </div>
  );
};

export default ForgetPassword;
